import {
  Menu as ReactMenu,
  FocusableItem as ReactMenuFocusableItem,
  MenuGroup as ReactMenuGroup,
  MenuItem as ReactMenuItem,
  MenuRadioGroup as ReactMenuRadioGroup,
} from '@szhsin/react-menu';
import cx from 'classnames';
import * as _ from 'lodash';
import { Children, cloneElement, isValidElement } from 'react';
import type { MenuGroupProps, MenuItemProps, MenuProps, MenuRadioGroupProps } from '@szhsin/react-menu';
import type { ChangeEventHandler, FunctionComponent, VoidFunctionComponent } from 'react';

import Badge from '../Badge/Badge';

import './Menu.less';
import './ReactMenu.less';
import Flex from '../Flex';
import Icon, { icons } from '../Icon';

import styles from './Menu.module.less';

export { default as StickyMenu } from './StickyMenu';
export { default as MenuSelectItem } from './MenuSelectItem';
export { MenuButton } from '@szhsin/react-menu';
export { ControlledMenu } from '@szhsin/react-menu';
export { SubMenu } from '@szhsin/react-menu';
export { FocusableItem } from '@szhsin/react-menu';
export { MenuDivider } from '@szhsin/react-menu';
export { MenuHeader } from '@szhsin/react-menu';

export const Menu = (props: MenuProps) => (
  <ReactMenu
    transition
    direction="right"
    setDownOverflow
    overflow="auto"
    position="auto"
    gap={8}
    {...props}
  />
);

type RadioMenuItemProps = Omit<MenuItemProps, 'type'> & { selected: boolean };

export const MenuRadioGroup = (props: MenuRadioGroupProps) => {
  const childrenWithProps = Children.map(props.children, (child) => {
    if (isValidElement<RadioMenuItemProps>(child)) {
      return cloneElement<RadioMenuItemProps>(child, {
        ...child.props,
        checked: child.props.value === props.value,
      });
    }
    return child;
  });

  return (
    <ReactMenuRadioGroup {..._.omit(props, 'children')}>{childrenWithProps}</ReactMenuRadioGroup>
  );
};

export const MenuGroup = (props: MenuGroupProps) => <ReactMenuGroup takeOverflow {...props} />;

type FossaSelectableItemProps = MenuItemProps & {
  count?: number;
};

//  This forms the "inner" content of a menu item, which is used for both the checkbox and radio items
//  It's also useful in cases where we want to render something static which looks like a menu item
export const StaticMenuItem: FunctionComponent<Pick<FossaSelectableItemProps, 'checked' | 'count' | 'className'>> = ({ children, ...props }) => (
  <Flex justify="space-between" grow={1} className={props.className as string}>
    <div>{children}</div>
    <Flex gap={8}>
      {props.checked && <Icon icon={icons.check} />}
      {!_.isNil(props.count) && (
        <Badge size="micro" color={props.checked ? 'light-green' : 'light-gray'}>
          {props.count}
        </Badge>
      )}
    </Flex>
  </Flex>
);

//  Base menu item that renders a checkbox or radio item.
export const BaseMenuItem: FunctionComponent<FossaSelectableItemProps> = ({ children, ...props }) => (
  <ReactMenuItem {...props} type={props.type ?? 'checkbox'}>
    <StaticMenuItem
      checked={props.checked}
      count={props.count}
    >
      {children}
    </StaticMenuItem>
  </ReactMenuItem>
);

export const CheckboxMenuItem: FunctionComponent<FossaSelectableItemProps> = (props) =>
  <BaseMenuItem {...props} type="checkbox" />;

export const RadioMenuItem: FunctionComponent<FossaSelectableItemProps> = (props) =>
  <BaseMenuItem {...props} type="radio" />;

export const MenuItem = (props: Omit<MenuItemProps, 'type'>) => <ReactMenuItem {...props} />;

/**
 * A search menu item that provides a search input field.
 */
export const SearchMenuItem: VoidFunctionComponent<{
  placeholder?: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  hasBorder?: boolean;
}> = ({ placeholder = 'Search...', onChange, hasBorder }) => {
  return (
    <ReactMenuFocusableItem className={styles.searchItem}>
      {({ ref }) => (
        <div className={styles.search}>
          <input
            ref={ref}
            type="text"
            placeholder={placeholder}
            onChange={onChange}
            className={cx(styles.searchInput, { [styles.searchInputDisableBorder]: !hasBorder })}
          />
          <span className={styles.searchIcon}>
            <Icon icon={icons.search} />
          </span>
        </div>
      )}
    </ReactMenuFocusableItem>
  );
};
